import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Users, Compass, ArrowRight, Brain, ShieldCheck, EyeOff, ThumbsUp, ThumbsDown, SlidersHorizontal } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <Icon size={20} strokeWidth={2.5} />
        <h2 className="font-display text-2xl">{title}</h2>
      </div>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

function Avatar({ person }) {
  return (
    <div className="w-12 h-12 rounded-full border-2 border-foreground overflow-hidden bg-sage flex items-center justify-center font-bold shrink-0">
      {person.avatar_path ? (
        <img src={fileUrl(person.avatar_path)} alt="" className="w-full h-full object-cover" />
      ) : (
        person.display_name?.[0] || "?"
      )}
    </div>
  );
}

function FeedbackControls({ onAction }) {
  return (
    <div className="flex gap-1 mt-3" aria-label="Recommendation controls">
      <button onClick={() => onAction("more")} className="brutal-btn bg-card px-2 py-1 text-[11px] flex items-center gap-1" title="More like this"><ThumbsUp size={11} /> More</button>
      <button onClick={() => onAction("less")} className="brutal-btn bg-card px-2 py-1 text-[11px] flex items-center gap-1" title="Less like this"><ThumbsDown size={11} /> Less</button>
      <button onClick={() => onAction("hide")} className="brutal-btn bg-card px-2 py-1 text-[11px] flex items-center gap-1" title="Hide this suggestion"><EyeOff size={11} /> Hide</button>
    </div>
  );
}

export default function ForYouPage() {
  const [brief, setBrief] = useState(null);
  const [people, setPeople] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [preferences, setPreferences] = useState({ discovery_mode: "balanced", muted_interests: [] });
  const [mutedText, setMutedText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [prefsBusy, setPrefsBusy] = useState(false);

  const loadRecommendations = async () => {
    const [briefRes, peopleRes, communityRes] = await Promise.all([
      api.get("/social-intelligence/brief"),
      api.get("/social-intelligence/people?limit=8"),
      api.get("/social-intelligence/communities?limit=8"),
    ]);
    setBrief(briefRes.data);
    setPeople(peopleRes.data || []);
    setCommunities(communityRes.data || []);
  };

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/social-intelligence/brief"),
      api.get("/social-intelligence/people?limit=8"),
      api.get("/social-intelligence/communities?limit=8"),
      api.get("/social-intelligence/preferences"),
    ])
      .then(([briefRes, peopleRes, communityRes, prefsRes]) => {
        if (!alive) return;
        setBrief(briefRes.data);
        setPeople(peopleRes.data || []);
        setCommunities(communityRes.data || []);
        const prefs = prefsRes.data || { discovery_mode: "balanced", muted_interests: [] };
        setPreferences(prefs);
        setMutedText((prefs.muted_interests || []).join(", "));
      })
      .catch(() => alive && setError("Your personalized HAEVEN view could not load right now."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const savePreferences = async (next) => {
    setPrefsBusy(true);
    try {
      const payload = {
        discovery_mode: next.discovery_mode || "balanced",
        muted_interests: next.muted_interests || [],
      };
      const r = await api.put("/social-intelligence/preferences", payload);
      setPreferences(r.data);
      setMutedText((r.data.muted_interests || []).join(", "));
      await loadRecommendations();
      toast.success("Discovery preferences updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update discovery preferences");
    } finally {
      setPrefsBusy(false);
    }
  };

  const changeMode = (mode) => savePreferences({ ...preferences, discovery_mode: mode });

  const saveMuted = () => {
    const muted = mutedText.split(",").map((value) => value.trim()).filter(Boolean);
    savePreferences({ ...preferences, muted_interests: muted });
  };

  const sendFeedback = async (targetType, targetId, action) => {
    try {
      await api.post("/social-intelligence/feedback", {
        target_type: targetType,
        target_id: targetId,
        action,
        reason: "",
      });
      if (action === "hide") {
        if (targetType === "user") setPeople((items) => items.filter((item) => item.id !== targetId));
        else setCommunities((items) => items.filter((item) => item.id !== targetId));
      }
      toast.success(action === "more" ? "HAEVEN will look for more like this" : action === "less" ? "HAEVEN will show less like this" : "Suggestion hidden");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save recommendation feedback");
    }
  };

  if (loading) {
    return <div className="brutal-card bg-card p-6 font-semibold">Building your HAEVEN…</div>;
  }

  return (
    <div className="space-y-8" data-testid="for-you-page">
      <section className="brutal-card bg-primary text-primary-foreground p-6 md:p-8 overflow-hidden relative">
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide mb-3">
            <Sparkles size={18} /> HAEVEN For You
          </div>
          <h1 className="font-display text-3xl md:text-5xl leading-tight">
            {brief?.headline || "A social network built around your life."}
          </h1>
          <p className="mt-4 text-sm md:text-base opacity-90 max-w-xl">
            {brief?.prompt || "Discover people and communities that fit what you care about."}
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold">
            <span className="px-3 py-1.5 rounded-full border-2 border-current">Connection over attention</span>
            <span className="px-3 py-1.5 rounded-full border-2 border-current">Explainable recommendations</span>
            <span className="px-3 py-1.5 rounded-full border-2 border-current">You stay in control</span>
          </div>
        </div>
      </section>

      {error && <div className="brutal-card bg-card p-4 text-sm font-semibold">{error}</div>}

      <section className="brutal-card bg-card p-5" data-testid="discovery-controls">
        <div className="flex items-center gap-2"><SlidersHorizontal size={18} /><h2 className="font-display text-xl">Control your discovery</h2></div>
        <p className="text-sm text-muted-foreground mt-1">Choose how familiar or exploratory suggestions should feel, and mute topics you do not want driving recommendations.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {[{ key: "familiar", label: "More familiar" }, { key: "balanced", label: "Balanced" }, { key: "explore", label: "Explore" }].map((mode) => (
            <button key={mode.key} disabled={prefsBusy} onClick={() => changeMode(mode.key)}
              className={`brutal-btn px-3 py-2 text-sm ${preferences.discovery_mode === mode.key ? "bg-foreground text-background" : "bg-card"}`}>
              {mode.label}
            </button>
          ))}
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <input className="brutal-input px-3 py-2 flex-1 min-w-[220px]" value={mutedText} onChange={(e) => setMutedText(e.target.value)} placeholder="Muted interests, comma separated" aria-label="Muted interests" />
          <button onClick={saveMuted} disabled={prefsBusy} className="brutal-btn bg-primary text-primary-foreground px-4 py-2">Save muted topics</button>
        </div>
      </section>

      <section>
        <SectionTitle
          icon={Users}
          title="People worth meeting"
          subtitle="Compatibility comes before follower counts. Every suggestion includes a reason."
        />
        {people.length ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {people.map((person) => (
              <div key={person.id} className="brutal-card bg-card p-4">
                <Link to={`/profile/${person.id}`} className="flex items-start gap-3 hover:-translate-y-0.5 transition-transform">
                  <Avatar person={person} />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{person.display_name || person.username || "HAEVEN member"}</div>
                    <p className="text-xs text-muted-foreground mt-1">{person.why}</p>
                    {!!person.shared_interests?.length && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {person.shared_interests.map((interest) => (
                          <span key={interest} className="text-[11px] border border-foreground rounded-full px-2 py-0.5">{interest}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ArrowRight size={17} className="shrink-0 mt-1" />
                </Link>
                <FeedbackControls onAction={(action) => sendFeedback("user", person.id, action)} />
              </div>
            ))}
          </div>
        ) : (
          <div className="brutal-card bg-card p-5 text-sm">Add interests during onboarding or adjust your discovery controls to improve matches.</div>
        )}
      </section>

      <section>
        <SectionTitle
          icon={Compass}
          title="Communities that fit you"
          subtitle="Find places to participate, not just content to consume."
        />
        {communities.length ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {communities.map((community) => (
              <div key={community.id} className="brutal-card bg-card p-4">
                <Link to={`/communities/${community.id}`} className="block hover:-translate-y-0.5 transition-transform">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bold">{community.name}</div>
                      <p className="text-xs text-muted-foreground mt-1">{community.why}</p>
                    </div>
                    <ArrowRight size={17} className="shrink-0" />
                  </div>
                  <div className="mt-3 text-xs font-semibold">{community.member_count || 0} members</div>
                </Link>
                <FeedbackControls onAction={(action) => sendFeedback("community", community.id, action)} />
              </div>
            ))}
          </div>
        ) : (
          <div className="brutal-card bg-card p-5 text-sm">No community matches yet. Explore HAEVEN or adjust discovery preferences.</div>
        )}
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="brutal-card bg-card p-5">
          <Brain size={22} />
          <h3 className="font-display text-xl mt-3">Why am I seeing this?</h3>
          <p className="text-sm text-muted-foreground mt-2">
            HAEVEN primarily uses interests you deliberately shared. Network signals are secondary, muted topics are excluded, and your feedback changes future ranking.
          </p>
        </div>
        <div className="brutal-card bg-card p-5">
          <ShieldCheck size={22} />
          <h3 className="font-display text-xl mt-3">Designed for healthy discovery</h3>
          <p className="text-sm text-muted-foreground mt-2">
            Blocked and hidden members are never suggested, moderation rules still apply, and recommendations optimize for compatibility rather than endless watch time.
          </p>
        </div>
      </section>
    </div>
  );
}
